import React, { useCallback, useMemo, useState } from 'react';
import { STRINGS, type Locale, type StringKey } from './strings';
import { I18nContext, type I18nContextValue, type TemplateVars } from './I18nContext';

function getInitialLocale(): Locale {
  const stored = localStorage.getItem('locale');
  if (stored === 'en' || stored === 'es') return stored;

  const navLang = navigator.language?.toLowerCase() ?? 'en';
  return navLang.startsWith('es') ? 'es' : 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => getInitialLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem('locale', next);
  }, []);

  const t = useCallback(
    (key: StringKey, vars?: TemplateVars) => {
      let text = STRINGS[locale][key];
      if (!vars) return text;

      for (const [name, value] of Object.entries(vars)) {
        text = text.replaceAll(`{${name}}`, String(value));
      }
      return text;
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
